import {
  ParlorHeadSection,
  CTypeMainHead,
  ATypeCTA,
  DTypeFAQ,
  CTypeMap,
  ATypeCardView,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  GOI_WEB_URL,
  BTypeBreadCrumb,
  DTypeCTA,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";
import { ParlorDetailProps, numberToLocalString, CTypeTabs } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos } = parlorRegion;

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (key === searchValue) return value;
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        companyname:
          getByValue(FUNERAL_SIDO, Number(parlor.sido)) +
          " " +
          getByValue(
            FUNERAL_SIGUNGU.get(Number(parlor.sido)) as any,
            Number(parlor.sigungu)
          ) +
          " 의 " +
          parlor.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code.split(" 의 ")[1];
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    parlor = parlors.find((parlor) => parlor.companyname === companyname);
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      description = `${parlor.companyname}에 대한 비용을 알아보세요. 근조화환, 실시간 빈소, 장례식장 위치, 영업 시간 등 해당 장례식장에 필요한 정보들을 알아보세요.`;
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 비용, ${parlor?.companyname}평균 예상 비용, ${parlor?.companyname} 무빈소, ${parlor?.companyname} 가족장, ${parlor?.companyname} 추천,
      ${parlor?.companyname} 시설, ${parlor?.companyname} 위치, ${parlor?.companyname} 주소, ${parlor?.companyname} 빈소, ${parlor?.companyname} 후기 ${parlor?.companyname} 영업시간`,
      pageOgTitle: `(${district}) ${parlor?.companyname} 위치, 시간, 시설, 정보 안내 :: 충청대전세종장례소개포털`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

const sangjoFAQData = [
  {
    question: "장례 비용/음식 비용을 최대한 줄일 수 있나요?",
    answer:
      "고이장례연구소(1666-9784)에서는 미사용 품목은 100% 공제해드리며, 품목별 가격 정찰제를 시행중입니다. 장례식장 비용 비교를 통하여 500만원이상 비용을 줄이며, 합리적으로 장례를 치를 수 있게 상담해드립니다. 특히, 음식 비용의 경우 2~300만원의 차이가 나기도 하는데 고이에서 파견한 장례도우미를 통하여 합리적으로 장례를 치를 수 있게 도와드립니다. <a href='https://www.goifuneral.co.kr/match/request/'>전체 비용 알아보기</a>",
  },
  {
    question: "장례지도사가 빈소에 함께하며 장지까지 동행하나요?",
    answer:
      "장례식장에 연락하여 장례를 진행하는 경우 장례 중 빈소를 잠깐 관리하는 역할을 합니다. 고이의 장례지도사는 장례식장부터 장지, 화장장 예약 등 장례 전반을 책임지며 합리적인 장례를 도와드립니다. 임종 순간부터 장지까지 동행하는 장례지도사, 미리 알아보세요. 고이장례연구소(1666-9784) <a href='https://www.goifuneral.co.kr/review/'>장례지도사 후기 보기</a>",
  },
  {
    question: "빈소를 미리 예약할 수 있나요?",
    answer:
      "아니요, 빈소는 임종 시점에 이용 가능 여부를 확인해야합니다. 특히 지나치게 큰 빈소를 이용하지 않도록 상담해드리고 염두에 두고 있는 빈소의 비용이나, 주변 장례식장 비용 비교까지 도와드립니다.",
  },
];
const faqList = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity:
    sangjoFAQData.map((item) => {
      return {
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      };
    }) || [],
};

export default function companyname({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu)
  );

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqList) }}
        /> */}
      </Head>
      <BTypeBreadCrumb
        firstItem={{
          text: `장례식장 추천`,
          href: GOI_WEB_URL + "/match/request/",
          event: {
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `${sidoName} 장례식장`,
          href: `/parlors-list/${sidoName} 의 장례식장 목록/`,
          event: {
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "br_crumb_sido_prlr_list",
          },
        }}
        thirdItem={{
          text: `${sigunguName} 장례식장`,
          href: `/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
          event: {
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "br_crumb_sigungu_prlr_list",
          },
        }}
        goiHomeEvent={{
          domain: "ch",
          path: "prlr_item_intro",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ParlorHeadSection
        component={
          <>
            <CTypeMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              mainColor={regionColor}
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname}`}
              managed_type={defaultParlor.managed_type}
              event={{
                domain: "ch",
                path: "prlr_item_intro",
                trigger_and_target: "uv_mth_req",
              }}
            />
          </>
        }
      />
      {/* <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
      
        <DTypeCTA
          sidoName={sidoName}
          sigunguName={sigunguName}
          mainColor={regionColor}
          companyname={defaultParlor.companyname}
          href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/`}
          event={{
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "CTA_uv_mth_req",
          }}
        />
      </Stack> */}
      <Stack style={{ background: "white" }}>
        <CTypeTabs
          mainColor={regionColor}
          href={`parlor-item/${sidoName} ${sigunguName} 의 ${defaultParlor.companyname}`}
          selectTab="소개"
          defaultParlor={defaultParlor}
          event={{
            domain: "ch",
            path: "prlr_item_intro",
            trigger_and_target: "CType_tab",
          }}
        />
      </Stack>

      <Stack
        style={{
          padding: "40px 20px",
        }}
      >
        <DTypeFAQ defaultParlor={defaultParlor} />
      </Stack>
      <Stack
        style={{
          background: regionColor.c100,

          padding: "40px 20px",
        }}
      >
        <CTypeMap defaultParlor={defaultParlor} />
      </Stack>

      <Stack
        style={{
          padding: "40px 20px",
        }}
      >
        <DTypeParlorList
          binsoAveragePrice
          sido={defaultParlor.sido}
          title={`${sidoName}의 다른 장례식장`}
          CardViewType={(res) => (
            <ATypeCardView
              key={res.companyname}
              name={res.companyname}
              href={`/parlor-item/${sidoName} ${sigunguName} 의 ${defaultParlor.companyname}/`}
              descriptions={[
                `빈소 평균 비용 : ${Number(
                  res.binso_average_charge
                ).toLocaleString()}원`,
              ]}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "ch",
                path: "prlr_item_intro",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
